// src/pages/PublicProfile.tsx
import {
  IonButton,
  IonCard,
  IonChip,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import {
  analyticsOutline,
  briefcaseOutline,
  codeSlashOutline,
  schoolOutline,
  ribbonOutline,
} from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios'; // Use a separate axios instance for public calls
import EmailVerificationModal from '../components/EmailVerificationModal.tsx';
import AccessRequestModal from '../components/AccessRequestModal.tsx';

// --- Data Interfaces ---
interface BasicInfo {
  name: string;
  email: string;
  phone: string;
  location: string;
}
interface Skill {
  name: string;
  category: string;
  relevance: number;
}
interface Experience {
  title: string;
  company: string;
  start_date: string;
  end_date: string;
  description: string;
  key_achievements: string[];
}
interface Education {
  institution: string;
  degree: string;
  field: string;
  start_date: string;
  end_date: string;
}
interface Certification {
  name: string;
  issuer: string;
  date: string;
}
interface ResumeAnalysisData {
  basic_info: BasicInfo;
  skills: Skill[];
  experience: Experience[];
  education: Education[];
  certifications: Certification[];
  summary: string;
}

// Professional Loading Component for Public Profile
const ProfessionalPublicLoader = () => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '50px',
    minHeight: '200px'
  }}>
    <div style={{ display: 'flex', gap: '4px', marginBottom: '24px' }}>
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          style={{
            width: '3px',
            height: '24px',
            background: 'linear-gradient(to bottom, #4A6FA5, #2D4A6B)',
            borderRadius: '2px',
            animation: 'professionalLoading 1.2s ease-in-out infinite',
            animationDelay: `${i * 0.1}s`
          }}
        />
      ))}
    </div>
    <p style={{ color: 'var(--app-text-color-secondary)', fontSize: '0.9rem', margin: 0 }}>
      Loading profile...
    </p>
  </div>
);

const SectionCard: React.FC<{ title: string; icon: string; children: React.ReactNode }> = ({ title, icon, children }) => (
  <IonCard style={{ marginTop: '16px', borderRadius: '12px', '--background': 'var(--app-card-background-color)' }}>
    <div style={{ padding: '16px', borderBottom: '1px solid var(--app-border-color)', display: 'flex', alignItems: 'center' }}>
      <IonIcon icon={icon} style={{ fontSize: '20px', color: 'var(--app-text-color-secondary)', marginRight: '12px' }} />
      <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--app-text-color)', margin: 0 }}>{title}</h3>
    </div>
    <div style={{ padding: '16px', color: 'var(--app-text-color-secondary)' }}>{children}</div>
  </IonCard>
);

interface GatedProfileData {
  access_required: boolean;
  access_control: string;
  title?: string;
  share_token: string;
}

const PublicProfile: React.FC = () => {
  const { shareToken } = useParams<{ shareToken: string }>();
  const [analysis, setAnalysis] = useState<ResumeAnalysisData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gatedProfile, setGatedProfile] = useState<GatedProfileData | null>(null);
  const [profileTitle, setProfileTitle] = useState<string | null>(null);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [showAccessRequest, setShowAccessRequest] = useState(false);
  const [sessionToken, setSessionToken] = useState<string | null>(null);

  // Initialize session token from localStorage
  useEffect(() => {
    if (shareToken) {
      const stored = localStorage.getItem(`session_${shareToken}`);
      if (stored) {
        setSessionToken(stored);
      }
    }
  }, [shareToken]);

  // Add CSS animations for professional loading
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes professionalLoading {
        0%, 40%, 100% { transform: scaleY(0.4); opacity: 0.5; }
        20% { transform: scaleY(1.0); opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => {
    const fetchPublicProfile = async () => {
      if (!shareToken) {
        setError('Share link is invalid.');
        setLoading(false);
        return;
      }
      try {
        const BASE_URL = process.env.REACT_APP_API_URL || 'https://api.autotuneresume.com/api/v1';
        const params = sessionToken ? { session_token: sessionToken } : {};
        const response = await axios.get(`${BASE_URL}/public/profile/${shareToken}`, { params });

        if (response.data.analysis) {
          // Successfully got profile data
          setAnalysis(response.data.analysis);
          setGatedProfile(null);
          // Store the title for display
          setProfileTitle(response.data.title || null);
        } else if (response.data.access_required) {
          // Profile is gated - show access options
          setGatedProfile(response.data);
          setAnalysis(null);
          setProfileTitle(response.data.title || null);
        }
      } catch (err: any) {
        if (err.response?.status === 403) {
          // Session token expired or invalid
          if (sessionToken) {
            localStorage.removeItem(`session_${shareToken}`);
            setSessionToken(null);
            // Retry without session token
            return;
          }
        }
        setError('Could not load profile. The link may be invalid or expired.');
      } finally {
        setLoading(false);
      }
    };
    fetchPublicProfile();
  }, [shareToken, sessionToken]);

  const handleEmailVerificationSuccess = (newSessionToken: string) => {
    setSessionToken(newSessionToken);
    localStorage.setItem(`session_${shareToken}`, newSessionToken);
    setShowEmailVerification(false);
    // Trigger a refresh of the profile
    setLoading(true);
  };

  const renderContent = () => {
    if (loading) return <ProfessionalPublicLoader />;
    if (error) return <div style={{ textAlign: 'center', padding: '50px', color: 'var(--ion-color-danger)' }}>{error}</div>;

    if (gatedProfile) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
          padding: '20px'
        }}>
          <IonCard style={{
            '--background': '#ffffff',
            border: '1px solid #e0e0e0',
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            padding: '48px 32px',
            maxWidth: '480px',
            width: '100%',
            textAlign: 'center'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              backgroundColor: '#000000',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px auto',
              position: 'relative'
            }}>
              <span style={{
                color: '#ffffff',
                fontSize: '24px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                lineHeight: '1'
              }}>🔒</span>
            </div>

            <h2 style={{
              fontSize: '1.75rem',
              fontWeight: '700',
              marginBottom: '12px',
              color: '#000000',
              letterSpacing: '-0.5px'
            }}>
              Access Required
            </h2>

            <p style={{
              color: '#666666',
              fontSize: '1rem',
              lineHeight: '1.5',
              marginBottom: '32px',
              maxWidth: '360px',
              margin: '0 auto 32px auto'
            }}>
              {gatedProfile.title ? `"${gatedProfile.title}"` : 'This profile'} requires verification to access.
            </p>

            {gatedProfile.access_control === 'email_verification' ? (
              <div>
                <p style={{
                  fontSize: '0.9rem',
                  color: '#888888',
                  marginBottom: '24px',
                  lineHeight: '1.4'
                }}>
                  Please verify your email address to view this profile.
                </p>
                <IonButton
                  expand="block"
                  onClick={() => setShowEmailVerification(true)}
                  style={{
                    '--background': '#000000',
                    '--background-hover': '#333333',
                    '--color': '#ffffff',
                    '--border-radius': '12px',
                    height: '48px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    textTransform: 'none',
                    letterSpacing: '0.5px'
                  }}
                >
                  Verify Email Address
                </IonButton>
              </div>
            ) : (
              <div>
                <p style={{
                  fontSize: '0.9rem',
                  color: '#888888',
                  marginBottom: '24px',
                  lineHeight: '1.4'
                }}>
                  Please request access from the profile owner.
                </p>
                <IonButton
                  expand="block"
                  onClick={() => setShowAccessRequest(true)}
                  style={{
                    '--background': '#000000',
                    '--background-hover': '#333333',
                    '--color': '#ffffff',
                    '--border-radius': '12px',
                    height: '48px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    textTransform: 'none',
                    letterSpacing: '0.5px'
                  }}
                >
                  Request Access
                </IonButton>
              </div>
            )}

            <div style={{
              marginTop: '32px',
              paddingTop: '24px',
              borderTop: '1px solid #f0f0f0'
            }}>
              <p style={{
                fontSize: '0.8rem',
                color: '#999999',
                margin: '0',
                fontWeight: '500'
              }}>
                Powered by <strong style={{ color: '#000000' }}>CareerTwins.ai</strong>
              </p>
            </div>
          </IonCard>
        </div>
      );
    }

    if (!analysis) return <div style={{ textAlign: 'center', padding: '50px' }}>Profile not found.</div>;

    return (
      <div>
        <IonCard style={{ '--background': 'var(--app-card-background-color)', padding: '32px 24px', textAlign: 'center' }}>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: 700,
            margin: '0 0 8px 0',
            color: '#1a1a1a',
            lineHeight: '1.2'
          }}>
            {analysis.basic_info.name}
          </h1>

          {profileTitle && (
            <h2 style={{
              fontSize: '1.3rem',
              fontWeight: 600,
              margin: '0 0 12px 0',
              color: '#4a5568',
              lineHeight: '1.3',
              letterSpacing: '0.25px'
            }}>
              {profileTitle}
            </h2>
          )}

          {analysis.basic_info.location && (
            <p style={{
              margin: '0',
              color: 'var(--ion-color-primary)',
              fontSize: '1.1rem',
              fontWeight: '500'
            }}>
              📍 {analysis.basic_info.location}
            </p>
          )}
        </IonCard>

        {analysis.summary && (
          <SectionCard title="Professional Summary" icon={analyticsOutline}>
            <p style={{ lineHeight: 1.6 }}>{analysis.summary}</p>
          </SectionCard>
        )}

        {analysis.skills && analysis.skills.length > 0 && (
          <SectionCard title="Skills" icon={codeSlashOutline}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {analysis.skills.map((skill, index) => (
                <IonChip key={`${skill.name}-${index}`} color={skill.category === 'technical' ? 'primary' : 'secondary'}>
                  {skill.name}
                </IonChip>
              ))}
            </div>
          </SectionCard>
        )}

        {analysis.experience && analysis.experience.length > 0 && (
          <SectionCard title="Experience" icon={briefcaseOutline}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {analysis.experience.map((exp, index) => (
                <div key={index} style={{ borderBottom: index < analysis.experience.length - 1 ? '1px solid var(--app-border-color)' : 'none', paddingBottom: index < analysis.experience.length - 1 ? '16px' : '0' }}>
                  <h4 style={{ fontSize: '1.1rem', fontWeight: '600', color: 'var(--app-text-color)', margin: '0 0 4px 0' }}>
                    {exp.title}
                  </h4>
                  <p style={{ fontSize: '0.95rem', color: 'var(--ion-color-primary)', margin: '0 0 8px 0', fontWeight: '500' }}>
                    {exp.company} • {exp.start_date} - {exp.end_date}
                  </p>
                  <p style={{ fontSize: '0.9rem', lineHeight: 1.5, margin: '0 0 8px 0' }}>
                    {exp.description}
                  </p>
                  {exp.key_achievements && exp.key_achievements.length > 0 && (
                    <ul style={{ fontSize: '0.9rem', lineHeight: 1.4, margin: '0', paddingLeft: '16px' }}>
                      {exp.key_achievements.map((achievement, achIndex) => (
                        <li key={achIndex} style={{ marginBottom: '4px' }}>{achievement}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </SectionCard>
        )}

        {analysis.education && analysis.education.length > 0 && (
          <SectionCard title="Education" icon={schoolOutline}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {analysis.education.map((edu, index) => (
                <div key={index}>
                  <h4 style={{ fontSize: '1.1rem', fontWeight: '600', color: 'var(--app-text-color)', margin: '0 0 4px 0' }}>
                    {edu.degree} in {edu.field}
                  </h4>
                  <p style={{ fontSize: '0.95rem', color: 'var(--ion-color-primary)', margin: '0 0 4px 0', fontWeight: '500' }}>
                    {edu.institution}
                  </p>
                  <p style={{ fontSize: '0.9rem', color: 'var(--app-text-color-secondary)', margin: '0' }}>
                    {edu.start_date} - {edu.end_date}
                  </p>
                </div>
              ))}
            </div>
          </SectionCard>
        )}

        {analysis.certifications && analysis.certifications.length > 0 && (
          <SectionCard title="Certifications" icon={ribbonOutline}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {analysis.certifications.map((cert, index) => (
                <div key={index}>
                  <h4 style={{ fontSize: '1.1rem', fontWeight: '600', color: 'var(--app-text-color)', margin: '0 0 4px 0' }}>
                    {cert.name}
                  </h4>
                  <p style={{ fontSize: '0.95rem', color: 'var(--ion-color-primary)', margin: '0 0 4px 0', fontWeight: '500' }}>
                    {cert.issuer}
                  </p>
                  <p style={{ fontSize: '0.9rem', color: 'var(--app-text-color-secondary)', margin: '0' }}>
                    {cert.date}
                  </p>
                </div>
              ))}
            </div>
          </SectionCard>
        )}
      </div>
    );
  };
  
  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Shared Profile</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding" style={{ '--background': 'var(--app-background-color)' }}>
        {renderContent()}
      </IonContent>

      {/* Email Verification Modal */}
      <EmailVerificationModal
        isOpen={showEmailVerification}
        onClose={() => setShowEmailVerification(false)}
        onVerificationSuccess={handleEmailVerificationSuccess}
        shareToken={shareToken || ''}
        profileTitle={gatedProfile?.title}
      />

      {/* Access Request Modal */}
      <AccessRequestModal
        isOpen={showAccessRequest}
        onClose={() => setShowAccessRequest(false)}
        shareToken={shareToken || ''}
        profileTitle={gatedProfile?.title}
      />
    </IonPage>
  );
};

export default PublicProfile;